import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import { connectAuthEmulator, getAuth, type Auth } from "firebase/auth";
import {
  connectFirestoreEmulator,
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from "firebase/firestore";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  appId: import.meta.env.VITE_FIREBASE_APP_ID || undefined,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || undefined,
};

export const useEmulators = import.meta.env.VITE_USE_EMULATORS === "true";

/** Same web config, exposed so admin provisioning can spin up a throwaway secondary app. */
export function getFirebaseConfig() {
  return firebaseConfig;
}

let app: FirebaseApp | undefined;
let auth: Auth | undefined;
let db: Firestore | undefined;

let authEmu = false;
let dbEmu = false;

export function getFirebaseApp(): FirebaseApp {
  if (!app) {
    app = getApps()[0] ?? initializeApp(firebaseConfig);
  }
  return app;
}

export function getFirebaseAuth(): Auth {
  if (!auth) {
    auth = getAuth(getFirebaseApp());
    if (useEmulators && !authEmu) {
      connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
      authEmu = true;
    }
  }
  return auth;
}

export function getDb(): Firestore {
  if (!db) {
    // Persistent (IndexedDB) cache — repeat visits/reloads paint from local cache while
    // revalidating in the background, instead of a cold network read every time. Falls back
    // to the default in-memory cache where persistence is unavailable (private mode, old browser).
    try {
      db = initializeFirestore(getFirebaseApp(), {
        localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
      });
    } catch {
      db = getFirestore(getFirebaseApp());
    }
    if (useEmulators && !dbEmu) {
      connectFirestoreEmulator(db, "127.0.0.1", 8080);
      dbEmu = true;
    }
  }
  return db;
}
